<template>
  <div>
    <div class="bg-custom-blue">
      <div class="container">
        <div class="row py-2">

          <!-- Filter Jahr  -->
          <div class="col-1">
            <select v-model="selectedYear" @change="onYearChange()" class="form-select form-select-sm">
              <option v-for="(n, i) in 11" :key="'actualYear' - (5 + i)" :value="((actualYear - 5) + i)">{{ (actualYear - 5) + i }}</option>
            </select>
          </div>

          <!-- Filter OrgUnit   -->
          <div class="col-2">
            <select v-model="filterOrgUnit" class="form-select form-select-sm">
              <option value=""> {{ $t("views.strategy.map.allOrgUnits") }} </option>
              <option v-for="ou in uniqueOrgUnits" :key="ou" :value="ou" > {{ ou }} </option>
            </select>
          </div>

          <!-- Suche nach Strategy-Name  -->
          <div class="col-3 ms-auto">
            <input v-model="filterName" type="text" class="form-control form-control-sm" :placeholder='$t("general.placeholderSearch")'>
          </div>

        </div>
      </div>
    </div>

    <!-- Title & Options  -->
    <div class="container my-2">
      <div class="row align-items-center">
        <div class="col d-flex align-items-center">
          <div class="h4 mb-1 mt-2">
            {{ $t("views.strategy.map.headingMap") }}
          </div>
        </div>
        <!-- Switch Status on/off -->
        <div class="col-auto">
          <div class="form-check form-switch"> <input class="form-check-input" type="checkbox" v-model="showStatus">
            <label class="form-check-label small"> {{ $t("views.strategy.map.showStatus") }} </label>
          </div>
        </div>

      </div>

      <!-- Strategy Map -->
      <div v-for="(strategies, parentName) in groupedStrategies" :key="parentName">

        <!-- Area & Line -->
        <div class="row mt-4">
          <div class="col">
            <h6 class="fs-09 text-secondary">{{ parentName }}</h6>
            <hr class="my-2" style="border-top: 1px solid #ccc; opacity: 1.4;" />
          </div>
        </div>

        <!-- Strategy Cards -->
        <div class="row row-cols-6 g-3">
          <div v-for="strategy in strategies" :key="strategy.id" class="col">
            <div class="card text-center shadow-sm">

              <!-- OrgUnit & ValidityPeriod-->
              <div class="card-header p-2 fs-08 foc-fw-500 d-flex justify-content-between align-items-center" :class="getPredictionColor(strategy.lastPrediction)">
                <span>{{ strategy.orgUnitName }}</span>
                <span>{{ getYear(strategy.validFrom) }} – {{ getYear(strategy.validTo) }} </span>
              </div>

              <!-- Title-->
              <div class="card-body p-2">
                <div class="d-flex align-items-center justify-content-between mt-0 mb-1">
                  <div class="flex-grow-1 text-center">
                    <span class="foc-fw-600 small strategy-title">{{ strategy.name }}</span>
                  </div>
                </div>

                <!-- Status & Implementation Counts-->
                <div v-if="showStatus" class="d-flex justify-content-around text-center small mt-2 mb-1">
                  <div>
                    <div class="text-muted small"> {{ $t("views.strategy.map.status") }} </div>
                    <svg class="bi" width="16" height="16" fill="currentColor">
                      <use :href="require('@/assets/icons/icons.svg') + '#harvey-' + strategy.lastConversion" :fill="getHarveyColor(strategy.lastPrediction)" :stroke="getHarveyColor(strategy.lastPrediction)"/>
                    </svg>
                  </div>
                  <div>
                    <div class="text-muted small"> {{ $t("views.strategy.map.objectiveCount") }} </div>
                    <strong>{{ strategy.objectiveCount > 0  ? strategy.objectiveCount : '-' }}</strong>
                  </div>
                  <div>
                    <div class="text-muted small"> {{ $t("views.strategy.map.projectCount") }} </div>
                    <strong>{{ strategy.projectCount > 0  ? strategy.projectCount : '-' }}</strong>
                  </div>
                  <div>
                    <div class="text-muted small"> {{ $t("views.strategy.map.measureCount") }} </div>
                    <strong>{{ strategy.measureCount > 0  ? strategy.measureCount : '-' }}</strong>
                  </div>
                </div>

                <!-- Links StrategyDetails & ImplementationModal & Initials-->
                <div class="d-flex justify-content-start gap-3 mt-2">
                  <a :href="createLink('strategy', strategy.id)" class="text-decoration-none fs-08" target="_blank">{{ $t("views.strategy.map.linkStrategy") }}</a>
                  <a v-if="strategy.objectiveCount > 0 || strategy.projectCount > 0 || strategy.measureCount > 0"
                    @click="selectStrategy(strategy.id)" href= "#strategyImplementationModal" class="text-decoration-none fs-08" data-bs-toggle="modal"> {{ $t("views.strategy.map.linkImplementation") }}
                  </a>
                  <span
                    v-else class="text-muted fs-08" style="cursor: not-allowed; opacity: 0.5;"> {{ $t('views.strategy.map.linkImplementation') }}
                  </span>
                  <div v-if="strategy.responsible && strategy.responsible !== ''" class="foc-avatar-circle ms-auto cursor-help" :title="strategy.responsible"> {{ getInitials(strategy.responsible) }} </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <StrategyImplementationModal></StrategyImplementationModal>
  </div>
</template>

<script>
import * as formatUtils from '@/composables/formatUtils'
import StrategyImplementationModal from '@/components/strategies/StrategyImplementationModal.vue'

export default {
  name: 'StrategiesStrategyMap',
  data () {
    return {
      selectedYear: this.$store.state.user?.settings?.favorites?.kpiDeviationSelectedYear ? this.$store.state.user.settings.favorites.kpiDeviationSelectedYear : new Date().getFullYear(),
      filterName: '',
      filterOrgUnit: '',
      showStatus: true,
      strategyMap: { strategies: [] },
      showImplementationModal: false,
      selectedStrategy: null
    }
  },
  components: {
    StrategyImplementationModal
  },
  computed: {
    filteredStrategies () {
      return this.strategyMap.strategies
        .filter(strategy =>
          strategy.name.toLowerCase().includes(this.filterName.toLowerCase())
        )
        .filter(strategy =>
          this.filterOrgUnit === '' ||
          (strategy.orgUnitName &&
          strategy.orgUnitName.toLowerCase().includes(this.filterOrgUnit.toLowerCase()))
        )
    },

    groupedStrategies () {
      const groups = {}

      for (const s of this.filteredStrategies) {
        const parent = s.parentStrategyName || 'Ohne Kategorie'

        if (!groups[parent]) {
          groups[parent] = []
        }

        groups[parent].push(s)
      }

      return groups
    },

    uniqueOrgUnits () {
      const units = this.strategyMap.strategies
        .map(s => s.orgUnitName)
        .filter(Boolean)

      return [...new Set(units)]
    },
    loadingStatus () {
      return this.$store.state.loadingStatus
    },
    actualYear () {
      const actualYear = new Date().getFullYear()
      return actualYear
    }
  },

  watch: {
    uniqueOrgUnits: {
      immediate: true,
      handler (list) {
        if (list && list.length > 0 && !this.filterOrgUnit) {
          this.filterOrgUnit = list[0] // erste OrgUnit als Default
        }
      }
    }
  },

  methods: {
    async onYearChange () {
      this.$store.commit('SET_LOADING_STATUS', true)
      const strategyMapData = await this.$store.dispatch('getStrategyMap', this.selectedYear)
      this.updateStrategyMap(strategyMapData)
      this.$store.commit('SET_LOADING_STATUS', false)
    },

    createLink (type, uuid) {
      let url = ''
      if (type === 'objective') url = '/objectives/manage/objectives?id=' + uuid
      if (type === 'project') url = '/projects/manage/projects?id=' + uuid
      if (type === 'kpi') url = '/monitoring/manage/kpis?id=' + uuid
      if (type === 'strategy') url = '/strategies/manage/strategies?id=' + uuid
      return url
    },
    formatPeriod (period) {
      return formatUtils.formatPeriod(period)
    },
    getInitials (name) {
      return formatUtils.getInitials(name)
    },
    updateStrategyMap (strategyMapData) {
      this.strategyMap = strategyMapData
    },
    unFormatNumber (number) {
      const userLocale = this.$store.state.user.settings?.language
      return formatUtils.unFormatNumber(userLocale, number)
    },
    cleanNodeName (name) {
      return formatUtils.cleanNodeName(name)
    },
    getHarveyColor (prediction) {
      return prediction === 'Y' ? '#198754' : prediction === 'D' ? '#ebad20' : '#dc3545'
    },
    getPredictionColor (prediction) {
      return prediction === 'Y' ? 'bg-success-subtle' : prediction === 'D' ? 'bg-warning-subtle' : 'bg-danger-subtle'
    },
    getYear (date) {
      return new Date(date).getFullYear()
    },
    async selectStrategy (strategyId) {
      this.$store.commit('SET_LOADING_STATUS', true)
      await this.$store.dispatch('getStrategy', strategyId)
      this.$store.commit('SET_LOADING_STATUS', false)
    }
  },
  async created () {
    this.$store.commit('SET_LOADING_STATUS', true)
    const strategyMapData = await this.$store.dispatch('getStrategyMap', this.selectedYear)
    this.updateStrategyMap(strategyMapData)
    this.$store.commit('SET_LOADING_STATUS', false)
  }
}
</script>

<style scoped>
.container {
  max-width: 1600px;
}
.col-kpi-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.select-th {
  background-color: transparent;
  border: 0;
  font-weight: bold;
}
.strategy-title {
  display: -webkit-box;
  -webkit-line-clamp: 2;     /* genau 2 Zeilen */
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  min-height: 2.4em;         /* erzwingt 2 Zeilen Höhe */
  line-height: 1.2em;
}

</style>
